import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { Popover } from 'antd';
import dayjs from 'dayjs';
import {
  FiFilter,
  FiDownload,
  FiGrid,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiCheckSquare,
  FiMoreVertical,
  FiEye,
} from 'react-icons/fi';

import { usePrevious } from '../../hooks/userPrevious';
import firebase from '../../config/firebase.config';
import { leaveActionTypes } from '@store/Leave/Leave.actionTypes';
import { teamActionTypes } from '@store/Team/Team.actionTypes';
import { getTeamMemberDetails } from '../../services/teamMemberDetails';
import { resolveDepartmentIcon } from '@/utils/departmentIcons';
import { escapeCSVField, downloadCSV } from '@/utils/csvExport';
import {
  LEAVE_TYPE_FILTER_OPTIONS,
  LEAVE_TYPE_TONE,
  LEAVE_STATUS_FILTER_OPTIONS,
  LEAVE_STATUS_TONE,
  parseLeaveDateString,
} from '@/utils/leaveTaxonomy';

import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { StatCard } from '@/components/StatCard';
import { StatusBadge, statusBadgeTones } from '@/components/StatusBadge';
import { InitialsBadge } from '@/components/InitialsBadge';
import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { DateRangePicker } from '@/components/DateRangePicker';
import { Button, buttonVariants } from '@/components/Button';
import { Table } from '@/components/Table';
import { Pagination } from '@/components/Pagination';
import { EmptyState } from '@/components/EmptyState';
import { Spinner } from '@/components/Spinner/Spinner.component';
import { ActionsMenu } from '@components/ActionsMenu';
import { addNotification, notificationStates } from '@components/Notification';

import { ViewLeaveModal } from '../Leave/ViewLeaveModal.component';
import styles from './LeaveRequest.module.scss';

const database = firebase.firestore();
const PAGE_SIZE = 10;

const STAT_CARDS_CONFIG = [
  {
    key: 'total',
    label: 'Total Requests',
    icon: <FiGrid />,
    iconBg: 'var(--purple-100)',
  },
  {
    key: 'pending',
    label: 'Pending',
    icon: <FiClock />,
    iconBg: 'var(--warning-100)',
  },
  {
    key: 'approved',
    label: 'Approved',
    icon: <FiCheckCircle />,
    iconBg: 'var(--success-100)',
  },
  {
    key: 'rejected',
    label: 'Rejected',
    icon: <FiXCircle />,
    iconBg: 'var(--error-100)',
  },
  {
    key: 'completed',
    label: 'Completed',
    icon: <FiCheckSquare />,
    iconBg: 'var(--blue-100)',
  },
];

function PlainCell({ children }) {
  return (
    <Text variant={textVariantsEnum.Text_sm} fontWeight={fontWeightEnum.Normal}>
      {children}
    </Text>
  );
}

function DateCell({ date }) {
  if (!date) return <PlainCell>—</PlainCell>;
  return (
    <div className={styles.dateCell}>
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Medium}
      >
        {date.toLocaleDateString(undefined, {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })}
      </Text>
      <Text
        variant={textVariantsEnum.Text_xs}
        fontWeight={fontWeightEnum.Normal}
        color="gray-500"
      >
        ({date.toLocaleDateString(undefined, { weekday: 'short' })})
      </Text>
    </div>
  );
}

function ReporterCell({ member }) {
  if (!member) return <PlainCell>—</PlainCell>;
  return (
    <div className={styles.personCell}>
      <InitialsBadge label={member.userName} twoLetter />
      <div className={styles.personCellText}>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Medium}
        >
          {member.userName}
        </Text>
        <Text
          variant={textVariantsEnum.Text_xs}
          fontWeight={fontWeightEnum.Normal}
          color="gray-500"
        >
          {member.userType || '—'}
        </Text>
      </div>
    </div>
  );
}

function DepartmentCell({ teamName }) {
  const { Icon, iconColor, iconBg } = resolveDepartmentIcon(teamName);
  return (
    <div className={styles.departmentCell}>
      <span
        className={styles.departmentIconWrap}
        style={{ backgroundColor: iconBg }}
      >
        <Icon size={14} color={iconColor} />
      </span>
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Medium}
      >
        {teamName || '—'}
      </Text>
    </div>
  );
}

export const LeaveRequestComponent = () => {
  const teams = useSelector((state) => ({
    projects: state.team.projects,
    active: state.team.active,
    teams: state?.team?.teams,
  }));
  const leaveState = useSelector((state) => state.leave || {});
  const { managerRequests, loading, approveLoading, rejectLoading } =
    leaveState;
  const { activeCompany } = useSelector((state) => state.auth.profile);
  const { profile } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const [processModal, setProcessModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [viewRequest, setViewRequest] = useState(null);
  const [activeMenuRow, setActiveMenuRow] = useState(null);
  const menuRef = useRef(null);
  const [companyMemberDetails, setCompanyMemberDetails] = useState({});
  const [teamNameMap, setTeamNameMap] = useState({});

  const [dateRange, setDateRange] = useState(() => [
    dayjs().startOf('month').toDate(),
    dayjs().endOf('month').toDate(),
  ]);
  const [typeFilter, setTypeFilter] = useState(LEAVE_TYPE_FILTER_OPTIONS[0]);
  const [statusFilter, setStatusFilter] = useState(
    LEAVE_STATUS_FILTER_OPTIONS[0]
  );
  const [departmentFilter, setDepartmentFilter] = useState(null);
  const [sortedBy, setSortedBy] = useState(null);
  const [reverseSort, setReverseSort] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Load member details + team names for all teams in company (requests can
  // reference any team's members) — one snapshot feeds both.
  useEffect(() => {
    if (!activeCompany?.companyId) return;
    database
      .collection('teamDatabase')
      .where('ownedBy', '==', activeCompany.companyId)
      .get()
      .then(async (snap) => {
        const names = {};
        snap.docs.forEach((doc) => {
          names[doc.id] = doc.data()?.teamName || doc.id;
        });
        setTeamNameMap(names);

        const teamIds = snap.docs.map((d) => d.id);
        const merged = {};
        await Promise.all(
          teamIds.map(async (teamId) => {
            const details = await getTeamMemberDetails(database, teamId);
            if (details && typeof details === 'object') {
              Object.assign(merged, details);
            }
          })
        );
        setCompanyMemberDetails(merged);
      })
      .catch(() => {
        setCompanyMemberDetails({});
        setTeamNameMap({});
      });
  }, [activeCompany?.companyId]);

  const departmentFilterOptions = useMemo(() => {
    const options = Object.entries(teamNameMap).map(([teamId, teamName]) => ({
      value: teamId,
      label: teamName,
    }));
    return [{ value: null, label: 'All Departments' }, ...options];
  }, [teamNameMap]);

  const getRegData = () => {
    if (!activeCompany?.companyId) return;
    dispatch({
      type: leaveActionTypes.GET_MANAGER_REQUESTS,
      payload: {
        managerId: profile.id,
        companyId: activeCompany.companyId,
      },
    });
  };

  const openRequestModal = (rowData) => {
    setSelectedData(rowData);
    setProcessModal(true);
  };

  const closeProcessModal = () => {
    if (!approveLoading && !rejectLoading) {
      setProcessModal(false);
      setSelectedData(null);
    }
  };

  const handleApprove = () => {
    if (!selectedData || !activeCompany?.companyId) return;
    dispatch({
      type: leaveActionTypes.APPROVE_REQUEST,
      payload: {
        companyId: activeCompany.companyId,
        requestId: selectedData.id,
        requestData: {
          ...selectedData,
          assignee: selectedData.assignee || profile.id,
        },
      },
    });
  };

  const handleReject = () => {
    if (!selectedData || !activeCompany?.companyId) return;
    dispatch({
      type: leaveActionTypes.REJECT_REQUEST,
      payload: {
        companyId: activeCompany.companyId,
        requestId: selectedData.id,
        assigneeId: profile.id,
      },
    });
  };

  // Close process modal when approve/reject saga completes
  const prevApproveLoading = usePrevious(approveLoading);
  const prevRejectLoading = usePrevious(rejectLoading);
  useEffect(() => {
    const approveJustFinished =
      prevApproveLoading === true && approveLoading === false;
    const rejectJustFinished =
      prevRejectLoading === true && rejectLoading === false;
    if (processModal && (approveJustFinished || rejectJustFinished)) {
      setProcessModal(false);
      setSelectedData(null);
    }
  }, [
    approveLoading,
    rejectLoading,
    processModal,
    prevApproveLoading,
    prevRejectLoading,
  ]);

  const ReporterTemplete = (rowData) => {
    const m = companyMemberDetails[rowData?.userId];
    return m?.userName || m?.userEmailid || rowData?.userId || '';
  };

  const onActionsButtonClick = (e, rowData) => {
    setActiveMenuRow(rowData);
    menuRef.current.toggle(e);
  };

  const actionsMenuItems = useMemo(() => {
    if (!activeMenuRow) return [];
    return [
      {
        label: 'View',
        icon: <FiEye size={16} />,
        onClick: () => {
          setViewRequest(activeMenuRow);
          menuRef.current?.hide();
        },
      },
    ];
  }, [activeMenuRow]);

  useEffect(() => {
    if (!isEmpty(teams.active)) {
      dispatch({
        type: teamActionTypes.GET_PROJECT,
        payload: { companyId: activeCompany.companyId },
      });
    }
    if (activeCompany?.companyId) {
      getRegData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.companyId]);

  const requests = useMemo(() => managerRequests || [], [managerRequests]);

  const dateFilteredRows = useMemo(() => {
    const [start, end] = dateRange || [];
    if (!start || !end) return requests;
    return requests.filter((row) => {
      const d = parseLeaveDateString(row.leaveDate);
      return Boolean(d) && d >= start && d <= end;
    });
  }, [requests, dateRange]);

  const filteredRows = useMemo(() => {
    return dateFilteredRows.filter((row) => {
      if (typeFilter?.value && row.type !== typeFilter.value) return false;
      if (statusFilter?.value && row.status !== statusFilter.value)
        return false;
      if (departmentFilter?.value && row.teamId !== departmentFilter.value)
        return false;
      return true;
    });
  }, [dateFilteredRows, typeFilter, statusFilter, departmentFilter]);

  const sortedRows = useMemo(() => {
    if (!sortedBy) return filteredRows;
    const getTime = (row) => {
      const date =
        sortedBy === 'leaveDate'
          ? parseLeaveDateString(row.leaveDate)
          : row.requestDate?.toDate?.();
      return date ? date.getTime() : 0;
    };
    const sorted = [...filteredRows].sort((a, b) => getTime(a) - getTime(b));
    return reverseSort ? sorted.reverse() : sorted;
  }, [filteredRows, sortedBy, reverseSort]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    dateRange,
    typeFilter,
    statusFilter,
    departmentFilter,
    sortedBy,
    reverseSort,
  ]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const pagedRows = sortedRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleSortClick = (id, reverse) => {
    setSortedBy(id);
    setReverseSort(reverse);
  };

  const stats = useMemo(() => {
    const approved = requests.filter((r) => r.status === 'Approved').length;
    const rejected = requests.filter((r) => r.status === 'Rejected').length;
    return {
      total: requests.length,
      pending: requests.filter((r) => r.status === 'Pending').length,
      approved,
      rejected,
      completed: approved + rejected,
    };
  }, [requests]);

  const exportToCSV = () => {
    if (sortedRows.length === 0) {
      addNotification({
        message: 'No data to export for the selected filters.',
        level: notificationStates.warning,
      });
      return;
    }
    const headers = [
      'ID',
      'Request Details',
      'Type',
      'Leave Date',
      'Request Date',
      'Reporter',
      'Department',
      'Status',
    ];
    const rows = sortedRows.map((row, index) => {
      const reportDate = row.requestDate?.toDate?.();
      return [
        escapeCSVField(index + 1),
        escapeCSVField(row.comment),
        escapeCSVField(row.type),
        escapeCSVField(row.leaveDate),
        escapeCSVField(reportDate ? reportDate.toLocaleDateString() : ''),
        escapeCSVField(ReporterTemplete(row)),
        escapeCSVField(teamNameMap[row.teamId] || row.teamId),
        escapeCSVField(row.status),
      ].join(',');
    });
    const date = new Date().toISOString().split('T')[0];
    downloadCSV({ headers, rows, filename: `leave-requests-team-${date}.csv` });
    addNotification({
      message: 'CSV exported successfully',
      level: notificationStates.success,
    });
  };

  const clearFilters = () => {
    setTypeFilter(LEAVE_TYPE_FILTER_OPTIONS[0]);
    setStatusFilter(LEAVE_STATUS_FILTER_OPTIONS[0]);
    setDepartmentFilter(null);
  };

  const filterPopoverContent = (
    <div className={styles.filterPopover}>
      <Dropdown
        label="Type"
        options={LEAVE_TYPE_FILTER_OPTIONS}
        value={typeFilter}
        onChange={setTypeFilter}
        selectStyles={borderedSelectStyles}
        isSearchable={false}
      />
      <Dropdown
        label="Status"
        options={LEAVE_STATUS_FILTER_OPTIONS}
        value={statusFilter}
        onChange={setStatusFilter}
        selectStyles={borderedSelectStyles}
        isSearchable={false}
      />
      <Dropdown
        label="Department"
        options={departmentFilterOptions}
        value={departmentFilter}
        onChange={setDepartmentFilter}
        selectStyles={borderedSelectStyles}
        isSearchable={false}
      />
      <Button
        text="Clear filters"
        variant={buttonVariants.ghost}
        onClick={clearFilters}
      />
    </div>
  );

  const columns = [
    {
      id: 'id',
      name: 'ID',
      style: { width: '4%' },
      cellRenderer: (row, rowIndex) => (
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Medium}
        >
          {(currentPage - 1) * PAGE_SIZE + rowIndex + 1}
        </Text>
      ),
    },
    {
      id: 'requestDetails',
      name: 'Request Details',
      style: { width: '10%' },
      cellRenderer: (row) => <PlainCell>{row.comment}</PlainCell>,
    },
    {
      id: 'type',
      name: 'Type',
      style: { width: '10%' },
      cellRenderer: (row) => (
        <StatusBadge
          label={row.type}
          tone={LEAVE_TYPE_TONE[row.type] || statusBadgeTones.default}
        />
      ),
    },
    {
      id: 'leaveDate',
      name: 'Leave Date',
      sortable: true,
      sortKey: 'leaveDate',
      style: { width: '10%' },
      cellRenderer: (row) => (
        <DateCell date={parseLeaveDateString(row.leaveDate)} />
      ),
    },
    {
      id: 'requestDate',
      name: 'Request Date',
      sortable: true,
      sortKey: 'requestDate',
      style: { width: '10%' },
      cellRenderer: (row) => <DateCell date={row.requestDate?.toDate?.()} />,
    },
    {
      id: 'reporter',
      name: 'Reporter',
      style: { width: '13%' },
      cellRenderer: (row) => (
        <ReporterCell member={companyMemberDetails[row.userId]} />
      ),
    },
    {
      id: 'department',
      name: 'Department',
      style: { width: '12%' },
      cellRenderer: (row) => (
        <DepartmentCell teamName={teamNameMap[row.teamId] || row.teamId} />
      ),
    },
    {
      id: 'status',
      name: 'Status',
      style: { width: '9%' },
      cellRenderer: (row) => (
        <StatusBadge
          label={row.status}
          tone={LEAVE_STATUS_TONE[row.status] || statusBadgeTones.default}
          dot
        />
      ),
    },
    {
      id: 'action',
      name: 'Action',
      style: { width: '13%' },
      cellRenderer: (row) => (
        <div className={styles.actionsCell}>
          {row.status === 'Pending' && (
            <Button
              variant={buttonVariants.primary_rounded}
              size="sm"
              text="Process"
              onClick={() => openRequestModal(row)}
              disabled={approveLoading || rejectLoading}
            />
          )}
          <Button
            icon={<FiMoreVertical size={14} />}
            variant={buttonVariants.outline}
            ariaLabel="Actions"
            onClick={(e) => onActionsButtonClick(e, row)}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <div className={styles.leaveRequestPage}>
        <div className={styles.leaveRequestContainer}>
          <div className={styles.leaveRequestHeader}>
            <DateRangePicker value={dateRange} onChange={setDateRange} />
            <div className={styles.leaveRequestHeaderActions}>
              <Popover
                content={filterPopoverContent}
                trigger="click"
                placement="bottomRight"
              >
                <Button
                  text="Filter"
                  icon={<FiFilter size={14} />}
                  variant={buttonVariants.outline}
                />
              </Popover>
              <Button
                text="Export"
                icon={<FiDownload size={14} />}
                variant={buttonVariants.outline}
                onClick={exportToCSV}
              />
            </div>
          </div>

          <div className={styles.statsRow}>
            {STAT_CARDS_CONFIG.map((card) => (
              <StatCard
                key={card.key}
                icon={card.icon}
                iconBg={card.iconBg}
                label={card.label}
                value={stats[card.key]}
              />
            ))}
          </div>

          <div className={styles.leaveRequestTableContainer}>
            {loading ? (
              <div className="w-100 text-center py-5">
                <Spinner />
              </div>
            ) : sortedRows.length === 0 ? (
              <EmptyState
                illustration
                title="No Leave Requests"
                description="No leave requests match the selected filters."
              />
            ) : (
              <>
                <Table
                  className={styles.leaveRequestTable}
                  data={pagedRows}
                  columns={columns}
                  onSortClick={handleSortClick}
                  sortedBy={sortedBy}
                  reverseSort={reverseSort}
                />
                <div className={styles.tableFooter}>
                  <Text variant={textVariantsEnum.Text_sm} color="gray-500">
                    Showing {(currentPage - 1) * PAGE_SIZE + 1} to{' '}
                    {Math.min(currentPage * PAGE_SIZE, sortedRows.length)} of{' '}
                    {sortedRows.length} requests
                  </Text>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <ViewLeaveModal
        isVisible={processModal}
        rowData={selectedData}
        reporterLabel={ReporterTemplete(selectedData)}
        departmentLabel={
          selectedData
            ? teamNameMap[selectedData.teamId] || selectedData.teamId
            : ''
        }
        title="Process Leave Request"
        onApprove={handleApprove}
        onDecline={handleReject}
        approveLoading={approveLoading}
        declineLoading={rejectLoading}
        onClose={closeProcessModal}
      />
      <ViewLeaveModal
        isVisible={Boolean(viewRequest)}
        rowData={viewRequest}
        reporterLabel={ReporterTemplete(viewRequest)}
        departmentLabel={
          viewRequest
            ? teamNameMap[viewRequest.teamId] || viewRequest.teamId
            : ''
        }
        onClose={() => setViewRequest(null)}
      />
      <ActionsMenu items={actionsMenuItems} ref={menuRef} />
    </>
  );
};

LeaveRequestComponent.displayName = 'LeaveRequest';
